import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Users, Search, Plus, MoreVertical, Pencil, Trash2, 
  Ban, CheckCircle, Eye, Calendar, Globe, Mail, Crown, ExternalLink, Blocks
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_COLORS = {
  // Current plans (source of truth: /api/plans/public)
  launch: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/40' },
  growth: { bg: 'bg-violet-500/20', text: 'text-violet-400', border: 'border-violet-500/40' },
  scale: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/40' },
  // Legacy plan slugs — kept only so historical tenants render with a badge color.
  // Not selectable in the create/edit dropdowns anymore.
  free: { bg: 'bg-slate-500/20', text: 'text-slate-400', border: 'border-slate-500/40' },
  starter: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/40' },
  pro: { bg: 'bg-violet-500/20', text: 'text-violet-400', border: 'border-violet-500/40' },
  enterprise: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/40' },
  demo: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/40' },
};

const STATUS_COLORS = {
  active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  suspended: { bg: 'bg-red-500/20', text: 'text-red-400' },
  cancelled: { bg: 'bg-slate-500/20', text: 'text-slate-400' }
};

export default function SuperAdminTenants() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [modulesDialogOpen, setModulesDialogOpen] = useState(false);
  const [modulesTenant, setModulesTenant] = useState(null);
  const [tenantModules, setTenantModules] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [planDefaultModules, setPlanDefaultModules] = useState([]);
  // Subscription plans fetched dynamically from /api/plans/public (single source
  // of truth — keeps this dialog aligned with the public pricing page).
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);

  const emptyForm = { name: '', slug: '', owner_email: '', owner_name: '', owner_password: '', plan: '', currency: 'INR', industry: '' };
  const [formData, setFormData] = useState(emptyForm);

  // Edit tenant state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({ name: '', plan: '', industry: '', owner_email: '' });
  const [editTenantId, setEditTenantId] = useState(null);

  useEffect(() => {
    fetchTenants();
  }, [statusFilter, planFilter, search]);

  // Load active subscription plans once
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await axios.get(`${API}/plans/public`);
        const active = (res.data || []).filter(p => p.is_active !== false);
        setSubscriptionPlans(active);
        // Pre-select the first plan in the create form so the Select renders a value
        if (active.length && !formData.plan) {
          setFormData(prev => ({ ...prev, plan: active[0].slug }));
        }
      } catch (e) {
        // Non-fatal — dialog will still open, dropdown will just be empty.
      }
    };
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper: format the dropdown label using the tenant's chosen currency
  const formatPlanLabel = (plan, currency) => {
    const price = currency === 'INR' ? plan.price_monthly_inr : plan.price_monthly;
    const symbol = currency === 'INR' ? '\u20B9' : '$';
    if (!price) return plan.name;
    return `${plan.name} (${symbol}${Number(price).toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US')}/mo)`;
  };

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('superAdminToken');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (planFilter !== 'all') params.append('plan', planFilter);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/platform/tenants?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
    } catch (error) {
      toast.error('Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('superAdminToken');
      await axios.post(`${API}/platform/tenants`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tenant created successfully');
      setCreateDialogOpen(false);
      resetCreateForm();
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    }
  };

  const handleSuspend = async (tenantId) => {
    try {
      const token = localStorage.getItem('superAdminToken');
      await axios.post(`${API}/platform/tenants/${tenantId}/suspend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tenant suspended');
      fetchTenants();
    } catch (error) {
      toast.error('Failed to suspend tenant');
    }
  };

  const handleActivate = async (tenantId) => {
    try {
      const token = localStorage.getItem('superAdminToken');
      await axios.post(`${API}/platform/tenants/${tenantId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tenant activated');
      fetchTenants();
    } catch (error) {
      toast.error('Failed to activate tenant');
    }
  };

  const handleDelete = async (tenantId) => {
    if (!window.confirm('Are you sure? This will permanently delete the tenant and ALL their data!')) return;
    try {
      const token = localStorage.getItem('superAdminToken');
      await axios.delete(`${API}/platform/tenants/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tenant deleted permanently');
      fetchTenants();
    } catch (error) {
      toast.error('Failed to delete tenant');
    }
  };

  const openEditDialog = (tenant) => {
    setEditTenantId(tenant.id);
    // Map legacy plan slugs to current plans so the Select renders correctly.
    const LEGACY_MAP = { free: 'launch', starter: 'launch', pro: 'growth', enterprise: 'scale' };
    const rawPlan = tenant.plan || (subscriptionPlans[0]?.slug ?? '');
    const safePlan = LEGACY_MAP[rawPlan] || rawPlan;
    setEditData({
      name: tenant.name || '',
      plan: safePlan,
      industry: tenant.industry || '',
      owner_email: tenant.owner_email || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditTenant = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('superAdminToken');
      await axios.put(`${API}/platform/tenants/${editTenantId}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tenant updated successfully');
      setEditDialogOpen(false);
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tenant');
    }
  };

  const handleViewDetails = async (tenant) => {
    setSelectedTenant(tenant);
    try {
      const token = localStorage.getItem('superAdminToken');
      const response = await axios.get(`${API}/platform/analytics/tenant/${tenant.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenantDetails(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch tenant details');
    }
  };

  // Build a fresh form, picking the first available plan as default
  const resetCreateForm = () => {
    const defaultPlan = subscriptionPlans[0]?.slug || '';
    setFormData({ ...emptyForm, plan: defaultPlan });
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const MODULE_LABELS = {
    hiretrack: 'HireTrack',
    recruiteriq: 'RecruiterIQ',
    nextmove: 'NextMove',
    hrms: 'HRMS',
    talentvault: 'TalentVault',
    knowledge_hub: 'Knowledge Hub',
    client_team: 'Client & Team Mgmt',
    administration: 'Administration',
    organization: 'Organization',
    analytics: 'Analytics',
  };

  const openModulesDialog = async (tenant) => {
    setModulesTenant(tenant);
    try {
      const token = localStorage.getItem('superAdminToken');
      const res = await axios.get(`${API}/platform/tenants/${tenant.id}/modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenantModules(res.data.enabled_modules || []);
      setAllModules(res.data.all_modules || []);
      setPlanDefaultModules(res.data.plan_default || []);
      setModulesDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch module settings');
    }
  };

  const toggleModule = (mod) => {
    setTenantModules(prev => prev.includes(mod) ? prev.filter(m => m !== mod) : [...prev, mod]);
  };

  const resetToPlanDefaults = () => {
    setTenantModules([...planDefaultModules]);
  };

  const saveModules = async () => {
    try {
      const token = localStorage.getItem('superAdminToken');
      await axios.put(`${API}/platform/tenants/${modulesTenant.id}/modules`, tenantModules, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success('Module permissions updated');
      setModulesDialogOpen(false);
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update modules');
    }
  };

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Tenant Management
          </h1>
          <p className="text-slate-400 mt-1">Manage all organizations on your platform</p>
        </div>
        <Button 
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          onClick={() => { resetCreateForm(); setCreateDialogOpen(true); }}
          data-testid="create-tenant-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Tenant
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-slate-900 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="tenant-search"
                placeholder="Search tenants..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40 bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                <SelectItem value="all" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">All Status</SelectItem>
                <SelectItem value="active" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Active</SelectItem>
                <SelectItem value="suspended" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Suspended</SelectItem>
              </SelectContent>
            </Select>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="w-full md:w-40 bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                <SelectItem value="all" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">All Plans</SelectItem>
                {subscriptionPlans.map(p => (
                  <SelectItem key={p.slug} value={p.slug} className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">
                    {p.name}
                  </SelectItem>
                ))}
                <SelectItem value="demo" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Demo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card className="bg-slate-900 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
          ) : tenants.length === 0 ? (
            <div className="text-center py-16">
              <Building2 className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No tenants found</p>
              <Button 
                className="mt-4 bg-indigo-600 hover:bg-indigo-700"
                onClick={() => { resetCreateForm(); setCreateDialogOpen(true); }}
              >
                Create First Tenant
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">Organization</TableHead>
                  <TableHead className="text-slate-400">Owner</TableHead>
                  <TableHead className="text-slate-400">Plan</TableHead>
                  <TableHead className="text-slate-400">Currency</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Users</TableHead>
                  <TableHead className="text-slate-400">Created</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.id} className="border-slate-700 hover:bg-slate-800/50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-600/20 rounded">
                          <Building2 className="h-4 w-4 text-indigo-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{tenant.name}</p>
                          <p className="text-xs text-slate-500">{tenant.slug}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-slate-300 text-sm">{tenant.owner_name}</p>
                        <p className="text-xs text-slate-500">{tenant.owner_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${PLAN_COLORS[tenant.plan]?.bg} ${PLAN_COLORS[tenant.plan]?.text} ${PLAN_COLORS[tenant.plan]?.border} border`}>
                        {tenant.plan === 'enterprise' && <Crown className="h-3 w-3 mr-1" />}
                        {tenant.plan.charAt(0).toUpperCase() + tenant.plan.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-300 text-sm font-medium">{tenant.currency || 'USD'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_COLORS[tenant.status]?.bg} ${STATUS_COLORS[tenant.status]?.text}`}>
                        {tenant.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-300">{tenant.user_count || 1}</TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      {new Date(tenant.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                          <DropdownMenuItem 
                            className="text-slate-300 hover:text-white hover:bg-slate-700"
                            onClick={() => navigate(`/super-admin/tenants/${tenant.slug}`)}
                          >
                            <ExternalLink className="mr-2 h-4 w-4" /> Manage Tenant
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-slate-300 hover:text-white hover:bg-slate-700"
                            onClick={() => handleViewDetails(tenant)}
                          >
                            <Eye className="mr-2 h-4 w-4" /> Quick View
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-slate-300 hover:text-white hover:bg-slate-700"
                            onClick={() => openEditDialog(tenant)}
                            data-testid={`edit-tenant-${tenant.slug}`}
                          >
                            <Pencil className="mr-2 h-4 w-4" /> Edit Details
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-indigo-400 hover:text-indigo-300 hover:bg-slate-700"
                            onClick={() => navigate(`/super-admin/tenants/${tenant.slug}/modules`)}
                            data-testid={`manage-modules-${tenant.slug}`}
                          >
                            <Blocks className="mr-2 h-4 w-4" /> Module Access Control
                          </DropdownMenuItem>
                          {tenant.status === 'active' ? (
                            <DropdownMenuItem 
                              className="text-amber-400 hover:text-amber-300 hover:bg-slate-700"
                              onClick={() => handleSuspend(tenant.id)}
                            >
                              <Ban className="mr-2 h-4 w-4" /> Suspend
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              className="text-emerald-400 hover:text-emerald-300 hover:bg-slate-700"
                              onClick={() => handleActivate(tenant.id)}
                            >
                              <CheckCircle className="mr-2 h-4 w-4" /> Activate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            className="text-red-400 hover:text-red-300 hover:bg-slate-700"
                            onClick={() => handleDelete(tenant.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Tenant Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Create New Tenant</DialogTitle>
            <DialogDescription className="text-slate-400">
              Set up a new organization on your platform
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTenant} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300 text-xs font-bold uppercase">Organization Name</Label>
                <Input
                  data-testid="tenant-name-input"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value, slug: generateSlug(e.target.value) });
                  }}
                  className="mt-2 bg-slate-800 border-slate-600 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs font-bold uppercase">Slug (URL)</Label>
                <Input
                  data-testid="tenant-slug-input"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="mt-2 bg-slate-800 border-slate-600 text-white"
                  pattern="[a-z0-9-]+"
                  required
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-xs font-bold uppercase">Industry</Label>
              <Input
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                placeholder="e.g., Technology, Healthcare, Finance"
                className="mt-2 bg-slate-800 border-slate-600 text-white"
              />
            </div>
            <div className="border-t border-slate-700 pt-4">
              <p className="text-sm text-slate-400 mb-3">Admin Account</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300 text-xs font-bold uppercase">Admin Name</Label>
                  <Input
                    data-testid="tenant-admin-name"
                    value={formData.owner_name}
                    onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                    className="mt-2 bg-slate-800 border-slate-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs font-bold uppercase">Admin Email</Label>
                  <Input
                    data-testid="tenant-admin-email"
                    type="email"
                    value={formData.owner_email}
                    onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                    className="mt-2 bg-slate-800 border-slate-600 text-white"
                    required
                  />
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-slate-300 text-xs font-bold uppercase">Admin Password</Label>
                <Input
                  data-testid="tenant-admin-password"
                  type="password"
                  value={formData.owner_password}
                  onChange={(e) => setFormData({ ...formData, owner_password: e.target.value })}
                  className="mt-2 bg-slate-800 border-slate-600 text-white"
                  minLength={6}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300 text-xs font-bold uppercase">Plan</Label>
                <Select value={formData.plan} onValueChange={(v) => setFormData({ ...formData, plan: v })}>
                  <SelectTrigger className="mt-2 bg-slate-800 border-slate-600 text-white" data-testid="tenant-plan-select">
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                    {subscriptionPlans.map(p => (
                      <SelectItem key={p.slug} value={p.slug} className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">
                        {formatPlanLabel(p, formData.currency)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-[11px] text-slate-500">Demo (non-chargeable) tenants are created from Demo Portals.</p>
              </div>
              <div>
                <Label className="text-slate-300 text-xs font-bold uppercase">Currency</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger className="mt-2 bg-slate-800 border-slate-600 text-white" data-testid="tenant-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                    <SelectItem value="INR" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">INR (&#8377;)</SelectItem>
                    <SelectItem value="USD" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">USD ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="border-slate-600 text-slate-300" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Create Tenant
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tenant Details Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-400" />
              {selectedTenant?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Tenant analytics and details
            </DialogDescription>
          </DialogHeader>
          {tenantDetails && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatBox label="Users" value={tenantDetails.users} />
                <StatBox label="Companies" value={tenantDetails.companies} />
                <StatBox label="Positions" value={tenantDetails.positions} />
                <StatBox label="Resumes" value={tenantDetails.resumes} />
                <StatBox label="Interviews" value={tenantDetails.interviews} />
                <StatBox label="Closures" value={tenantDetails.closures} />
                <StatBox label="Joinings" value={tenantDetails.joinings} />
              </div>
              <div className="border-t border-slate-700 pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Owner:</span>
                    <span className="text-white ml-2">{selectedTenant?.owner_email}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Plan:</span>
                    <Badge className={`ml-2 ${PLAN_COLORS[selectedTenant?.plan]?.bg} ${PLAN_COLORS[selectedTenant?.plan]?.text}`}>
                      {selectedTenant?.plan}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-slate-500">Database:</span>
                    <span className="text-slate-400 ml-2 font-mono text-xs">{selectedTenant?.db_name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Created:</span>
                    <span className="text-white ml-2">{new Date(selectedTenant?.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modules Dialog */}
      <Dialog open={modulesDialogOpen} onOpenChange={setModulesDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Blocks className="h-5 w-5 text-indigo-400" /> Module Access
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {modulesTenant?.name} — {modulesTenant?.plan?.charAt(0).toUpperCase()}{modulesTenant?.plan?.slice(1)} Plan
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-slate-500">Toggle modules this tenant can access</p>
              <Button
                variant="ghost" size="sm"
                className="text-xs text-indigo-400 hover:text-indigo-300"
                onClick={resetToPlanDefaults}
                data-testid="reset-modules-btn"
              >
                Reset to Plan Defaults
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {allModules.map(mod => {
                const isEnabled = tenantModules.includes(mod);
                const isPlanDefault = planDefaultModules.includes(mod);
                return (
                  <button
                    key={mod}
                    onClick={() => toggleModule(mod)}
                    data-testid={`module-toggle-${mod}`}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                      isEnabled
                        ? 'bg-indigo-500/10 border-indigo-500/40 text-white'
                        : 'bg-slate-800/50 border-slate-700 text-slate-500'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center transition-all ${
                      isEnabled ? 'bg-indigo-500 border-indigo-500' : 'border-slate-600'
                    }`}>
                      {isEnabled && <CheckCircle className="h-3 w-3 text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{MODULE_LABELS[mod] || mod}</p>
                      {isPlanDefault && <p className="text-[10px] text-slate-500">Plan default</p>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setModulesDialogOpen(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={saveModules} className="bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="save-modules-btn">
              Save Modules
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Pencil className="h-5 w-5 text-indigo-400" /> Edit Tenant
            </DialogTitle>
            <DialogDescription className="text-slate-400">Update tenant details. Slug cannot be changed after creation.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditTenant} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Organization Name</Label>
              <Input data-testid="edit-tenant-name" value={editData.name} onChange={e => setEditData(p => ({...p, name: e.target.value}))} className="mt-1 bg-slate-800 border-slate-700 text-white" required />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Owner Email</Label>
              <Input data-testid="edit-tenant-email" value={editData.owner_email} onChange={e => setEditData(p => ({...p, owner_email: e.target.value}))} className="mt-1 bg-slate-800 border-slate-700 text-white" type="email" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Plan</Label>
                <Select value={editData.plan} onValueChange={v => setEditData(p => ({...p, plan: v}))}>
                  <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                    {subscriptionPlans.map(p => (
                      <SelectItem key={p.slug} value={p.slug} className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Industry</Label>
                <Input value={editData.industry} onChange={e => setEditData(p => ({...p, industry: e.target.value}))} className="mt-1 bg-slate-800 border-slate-700 text-white" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="border-slate-600 text-slate-300" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-3 text-center border border-slate-700">
      <div className="text-2xl font-bold text-white">{value || 0}</div>
      <div className="text-xs text-slate-400">{label}</div>
    </div>
  );
}
